import React, { useEffect, useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Cookies from 'universal-cookie';
import {
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    DialogContentText,
    Grid,
    IconButton,
    Link,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack, PersonAdd } from '@material-ui/icons';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Auth } from 'aws-amplify';
import { host, units_settings } from "../../data";
import { isValidEmail } from '../../helpers/isValidEmail';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    root: {
    },
    indented: {
        marginLeft: theme.spacing(10),
    },
    content: {
        padding: theme.spacing(2),
    },
    rootpaper: {
        width: 1000,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        overflowX: "auto"
    },
    dropdown: {
        display: 'flex',
        marginLeft: 'auto',
    },
    fluidtable: {
        height: '60vh',
    },
    table: {
        width: '100%',
        height: '40vh',
    },
    grow: {
        flexGrow: 1,
    },
    iconButtons: {
        marginRight: theme.spacing(1),
    },
    link: {
        color: theme.palette.info.dark,
    },
    api: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        overflowX: "auto",
        float: "right",
    },
}));

export default function OrgDetails({ orgInfo }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const matches = useMediaQuery('(max-width:600px)');
    const [displayUnits, setDisplayUnits] = useState('US');
    const [branches, setBranches] = useState([]);
    const [fluids, setFluids] = useState([]);
    const [dataReady, setDataReady] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [isApiKeyActive, setIsApiKeyActive] = useState(true);
    const recipient = useRef("");
    const [branchCol, setBranchCol] = useState([]);

    const baseurl = '/orgs/' + orgInfo.orgName;

    const fluidColumns = [
        {
            headerName: t("fluidName"),
            field: 'name',
            width: 110,
            disableClickEventBubbling: true
        },
        {
            headerName: t("monitoredTanks"),
            field: 'tankCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("inventory"),
            field: 'volume_sum_disp',
            sortComparator: (v1, v2, param1, param2) => param1.row.volume_sum - param2.row.volume_sum,
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("ullage"),
            field: 'ullage_sum_disp',
            sortComparator: (v1, v2, param1, param2) => param1.row.ullage_sum - param2.row.ullage_sum,
            flex: 1,
            disableClickEventBubbling: true
        },
    ];

    const [fluidCol, setFluidCol] = useState(fluidColumns);

    const branchColumns = [
        {
            headerName: t("branch"),
            field: 'branchName',
            renderCell: renderBranchLink,
            width: 104,
            disableClickEventBubbling: true
        },
        {
            headerName: t("sites"),
            field: 'siteCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("tanks"),
            field: 'tankCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("criticalLevel"),
            field: 'level_critical_alarmCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("levelAlarms"),
            field: 'level_alarmCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("commAlarms"),
            field: 'comm_alarmCount',
            flex: 1,
            disableClickEventBubbling: true
        },
        {
            headerName: t("lowBattery"),
            field: 'battery_alarmCount',
            flex: 1,
            disableClickEventBubbling: true
        },
    ];

    useEffect(() => {
        setBranchCol(branchColumns);
    }, []);

    const mapFunctionSetWidth = (width) => (el, index) => {
        const { flex, ...rest } = el;
        if (index !== 0) {
            return { ...rest, width }
        }
        return el;
    };

    useEffect(() => {
        if (matches) {
            setFluidCol(prevState => prevState.map(mapFunctionSetWidth(140)));
            setBranchCol(prevState => prevState.map(mapFunctionSetWidth(130)));
        }
    }, [matches]);

    function fetchOrgDetails() {
        let cookies = new Cookies();
        let tDisplayUnits = cookies.get("DisplayUnits");

        if (undefined !== tDisplayUnits) {
            setDisplayUnits(tDisplayUnits);
        }

        Auth.currentAuthenticatedUser()
            .then(data => {
                let requestOptions = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    },
                };
                console.log('fetching org details...');
                fetch(host + '/orgs/' + orgInfo.orgId + '/details/', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        setApiKey(data.apiKey);
                        setIsApiKeyActive(data.isApiKeyActive);

                        var maxBranchNameLength = 13;
                        var maxFluidNameLength = 13;

                        for (var i = 0; i < data.branchesList.length; ++i) {
                            try {
                                if (data.branchesList[i].branchName.length > maxBranchNameLength) {
                                    maxBranchNameLength = data.branchesList[i].branchName.length;
                                }
                            } catch (error) {
                            }
                        }

                        for (var i = 0; i < data.fluidsList.length; ++i) {
                            try {
                                if (data.fluidsList[i].name.length > maxFluidNameLength) {
                                    maxFluidNameLength = data.fluidsList[i].name.length;
                                }
                            } catch (error) {
                            }
                        }

                        setBranchCol(prevState => prevState.map((el, index) => {
                            if (index === 0) {
                                return { ...el, width: maxBranchNameLength * 9 + 9 }
                            }
                            return el;
                        }));
                        setFluidCol(prevState => prevState.map((el, index) => {
                            if (index === 0) {
                                return { ...el, width: maxFluidNameLength * 9 + 9 }
                            }
                            return el;
                        }));
                        setBranches(data.branchesList);
                        setFluids(data.fluidsList);
                    }).catch((error) => {
                        console.log(error);
                    })
                setDataReady(true);
            });
    }

    useEffect(() => {
        fetchOrgDetails();
    }, [orgInfo]);

    function renderBranchLink(params) {
        if (params.row.branchName === undefined) {
            return;
        }

        return (
            <Link className={classes.link}
                component={RouterLink}
                to={baseurl.concat('/branches/').concat(params.row.branchName)}>
                {params.value}
            </Link>
        );
    }

    function UpdateVolumeUnits() {
        var fluidsList = fluids;
        const vol_string = displayUnits.includes("US") ? "gal" : "L";
        if (fluids !== undefined) {
            for (var i = 0; i < fluidsList.length; i++) {
                if (displayUnits.includes("Metric")) {
                    if (fluidsList[i].VolumeUnit === 'gallons') {
                        fluidsList[i].volume_sum = Math.round((fluids[i].volume_sum) * 3.78541);
                        fluidsList[i].ullage_sum = Math.round((fluids[i].ullage_sum) * 3.78541);
                        fluidsList[i].VolumeUnit = 'liters'
                    }
                }
                else {
                    if (fluidsList[i].VolumeUnit === 'liters') {
                        fluidsList[i].volume_sum = Math.round((fluids[i].volume_sum) / 3.78541);
                        fluidsList[i].ullage_sum = Math.round((fluids[i].ullage_sum) / 3.78541);
                        fluidsList[i].VolumeUnit = 'gallons'
                    }
                }
                fluidsList[i].volume_sum_disp = "{vol} {unit}".replace("{vol}", fluidsList[i].volume_sum.toFixed(0)).replace("{unit}",vol_string);
                fluidsList[i].ullage_sum_disp = "{vol} {unit}".replace("{vol}", fluidsList[i].ullage_sum.toFixed(0)).replace("{unit}",vol_string);
            }
            setFluids(fluidsList);
        }
    }

    useEffect(() => {
        UpdateVolumeUnits();
    }, [fluids, displayUnits]);

    const handleSetDisplayUnits = (newSetting) => {
        var cookies = new Cookies();
        cookies.set('DisplayUnits', newSetting, { path: "/", maxAge: 1000000 });
        setDisplayUnits(newSetting);
    };

    const handleInviteOpen = () => {
        setInviteOpen(true);
    };

    const handleInviteClose = () => {
        setInviteOpen(false);
    };

    function isValidEnteredEmail(value) {
        if (isValidEmail(value))
            return true;

        alert(t("youHaveEnteredAnInvalidEmail"));
        return false;
    }

    // Functions to handle invitations
    const handleInvite = async (permissions) => {
        var recip = recipient.current.value.trim().replace(/,{1,}$/, '');
        recip = recip.trim();
        var recipientArr = recip.split(',');
        var sent = false;

        for (var index = 0; index < recipientArr.length; ++index) {
            var userToInvite = recipientArr[index];

            if (isValidEnteredEmail(userToInvite)) {
                try {
                    // Get current authenticated user session
                    let user = await Auth.currentAuthenticatedUser();
                    user.getSession((err, session) => {
                        if (err) {
                            console.log(err);
                            return;
                        }

                        setInviteOpen(false);

                        var body = JSON.stringify(
                            {
                                "Recipient": userToInvite,
                                "Id": orgInfo.orgId,
                                "Role": permissions,
                                'User': user.attributes.email
                            }
                        );

                        var requestOptions = {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': session.getIdToken().jwtToken
                            },
                            body: body
                        };

                        fetch(host + '/orgs/' + orgInfo.orgId + '/invite', requestOptions)
                            .then(async response => {
                                const data = await response.json();
                                if (response.status == 200) {
                                    if (!sent) {
                                        alert(t("invitationHasBeenSent"));
                                        sent = true;
                                    }
                                }
                                else {
                                    alert(data);
                                }
                            });
                    });
                } catch (e) {
                    console.log(e);
                }
            }
        }
    };

    const deleteAPIKey = async () => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            user.getSession((err, session) => {
                if (err) {
                    console.log(err);
                    return;
                }

                const sessionToken = session.getIdToken().jwtToken;
                var body = JSON.stringify(
                    {
                    }
                );

                var requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': sessionToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/api-key', requestOptions)
                    .then(async response => {
                        return { body: await response.json(), status: response.status }
                    })
                    .then(data => {
                        setApiKey("")
                        setIsApiKeyActive(false);
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            });
        } catch (e) {
            console.log(e);
        };
    };

    const createAPIKey = async () => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            user.getSession((err, session) => {
                if (err) {
                    console.log(err);
                    return;
                }

                const sessionToken = session.getIdToken().jwtToken;
                var body = JSON.stringify(
                    {
                    }
                );

                var requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': sessionToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/api-key', requestOptions)
                    .then(async response => {
                        return { body: await response.json(), status: response.status }
                    })
                    .then(data => {
                        setApiKey(data.body.api_key)
                        setIsApiKeyActive(true);
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            });
        } catch (e) {
            console.log(e);
        };
    };

    const deactivateAPIKey = async () => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            user.getSession((err, session) => {
                if (err) {
                    console.log(err);
                    return;
                }

                const sessionToken = session.getIdToken().jwtToken;
                var body = JSON.stringify(
                    {
                        'Active': false
                    }
                );

                var requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': sessionToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/api-key', requestOptions)
                    .then(async response => {
                        return { body: await response.json(), status: response.status }
                    })
                    .then(data => {
                        setIsApiKeyActive(false);
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            });
        } catch (e) {
            console.log(e);
        };
    };

    const activateAPIKey = async () => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            user.getSession((err, session) => {
                if (err) {
                    console.log(err);
                    return;
                }

                const sessionToken = session.getIdToken().jwtToken;
                var body = JSON.stringify(
                    {
                        'Active': true
                    }
                );

                var requestOptions = {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': sessionToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/api-key', requestOptions)
                    .then(async response => {
                        return { body: await response.json(), status: response.status }
                    })
                    .then(data => {
                        setIsApiKeyActive(true);
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            });
        } catch (e) {
            console.log(e);
        };
    };

    if (!dataReady) {
        return (<div><Typography align="center">{t("loadingData")}</Typography></div>)
    }

    return (
        <Grid container
            alignItems='stretch'
            direction='column'
            justify='flex-start'
            className={classes.content}>
            <Grid item xs>
                <Toolbar>
                    <Tooltip title={t("goHome")}>
                        <IconButton color='primary'
                            component={RouterLink}
                            to={'/'}>
                            <ArrowBack />
                        </IconButton>
                    </Tooltip>
                    <Breadcrumbs>
                        <Link variant='h5'
                            component={RouterLink}
                            to={'/'}>
                            {orgInfo.orgName}
                        </Link>
                        <Typography variant="h5">
                            {t("dashboard")}
                        </Typography>
                    </Breadcrumbs>
                    <div className={classes.grow} />
                    <Tooltip title={t("refresh")}>
                        <IconButton aria-haspopup='true'
                            color='inherit'
                            className={classes.iconButtons}
                            onClick={fetchOrgDetails}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("invite")}>
                        <IconButton aria-haspopup='true'
                            color='inherit'
                            className={classes.iconButtons}
                            onClick={handleInviteOpen}>
                            <PersonAdd />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </Grid>
            <Grid item xs>
                <div className={classes.api}>
                    {apiKey}

                    {apiKey == "" ?
                        <Button color='secondary' onClick={createAPIKey}>
                            {t("createNewApiKey")}
                        </Button>
                        :
                        <span>
                            <Button color='primary' onClick={deleteAPIKey}>
                                {t("deleteApiKey")}
                            </Button>
                            {!isApiKeyActive ?
                                <Button color='secondary' onClick={activateAPIKey}>
                                    {t("enableApiKey")}
                                </Button>
                                :
                                <Button color='secondary' onClick={deactivateAPIKey}>
                                    {t("disableApiKey")}
                                </Button>
                            }
                        </span>
                    }
                </div>
            </Grid>
            <Grid item xs>
                <div style={{}}>
                    <div className={classes.table}>
                        <DataGrid hideFooterSelectedRowCount
                            rowsPerPageOptions={[10, 25, 50]}
                            pagination
                            pageSize={10}
                            rows={branches}
                            columns={branchCol} />
                    </div>
                </div>

            </Grid>
            <Grid item xs>
                <Toolbar>
                    <Typography></Typography>
                    <Select className={classes.dropdown}
                        id="units-dropdown"
                        value={displayUnits}
                        onChange={(event) => handleSetDisplayUnits(event.target.value)}
                    >
                        {units_settings.map((Uval) => (
                            <MenuItem key={Uval.id} value={Uval.name}>
                                <ListItemText primary={t(Uval.label)} />
                            </MenuItem>
                        ))}
                    </Select>
                </Toolbar>
            </Grid>
            <Grid item xs>
                <div className={classes.fluidtable}>
                    <DataGrid
                        hideFooterSelectedRowCount
                        disableColumnMenu={true}
                        rowsPerPageOptions={[25, 50, 100]}
                        pagination
                        pageSize={100}
                        rows={fluids}
                        columns={fluidCol}
                        onResize={(d) => console.log('dsasfdafdsa')}
                    />
                </div>
            </Grid>
            <Dialog open={inviteOpen} onClose={handleInviteClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Invite</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("toInviteAUser")}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        inputRef={recipient}
                        label={t("emailAddress")}
                        type="email"
                        fullWidth />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleInviteClose} color="primary">
                        {t("cancel")}
                    </Button>
                    <Button onClick={() => handleInvite("Viewer")} variant="contained" color="primary">
                        {t("asViewer")}
                    </Button>
                    <Button onClick={() => handleInvite("Editor")} variant="contained" color="primary">
                        {t("asEditor")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}